import { Grid, Link, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FieldWrapper } from 'DesignSystem';
import ThemeButton from 'DesignSystem/Buttons/ThemeButton/ThemeButton';
import { FastField as Field, Form, Formik } from 'formik';
import { TextField } from 'formik-mui';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import * as Yup from 'yup';
import AcceptTermsCheckbox from '../AcceptTermsCheckbox';
import AuthHeadline from '../AuthHeadline';
import PasswordInputField from './PasswordInputField';
import TermsDialog from '../Dialogs/TermsDialog';

const Text = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
}));

const RegistrationSchema = Yup.object().shape({
  password: Yup.string()
    .default('')
    .required('This field is required.')
    .min(8, 'Please enter at least 8 characters')
    .matches(/^(?=.*[a-z])/, 'Password must contain at least one lowercase letter.')
    .matches(/^(?=.*[A-Z])/, 'Password must contain at least one uppercase letter.')
    .matches(/^(?=.*\d)/, 'Password must contain at least one number.')
    .matches(
      /^(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/,
      'Password must contain at least one special character.'
    ),
  confirmPassword: Yup.string()
    .default('')
    .required('This field is required.')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
  firstName: Yup.string().required('This field is required.'),
  lastName: Yup.string().required('This field is required.'),
  termsAccepted: Yup.bool()
    .oneOf([true], 'Please accept the terms and conditions')
    .required('Please accept the terms and conditions'),
});

const RegistrationInvitedForm = ({ onSubmit }) => {
  const [termsOpen, setTermsOpen] = useState(false);

  return (
    <Formik
      initialValues={{
        firstName: '',
        lastName: '',
        password: '',
        termsAccepted: false,
      }}
      validateOnBlur={false}
      validateOnChange={true}
      validationSchema={RegistrationSchema}
      onSubmit={values => onSubmit(values)}
    >
      {({ isSubmitting }) => (
        <Form>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <AuthHeadline text="Welcome to" highlighted="the transparency tool" />
              <Text variant="body2">
                You have been invited to join the platform! <br /> Enter your details to register.
              </Text>
            </Grid>
            <Grid item sm={12} md={10}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <FieldWrapper label="First name">
                    <Field
                      component={TextField}
                      name="firstName"
                      placeholder="Jane"
                      variant="outlined"
                    />
                  </FieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <FieldWrapper label="Last name">
                    <Field
                      component={TextField}
                      placeholder="Doe"
                      name="lastName"
                      variant="outlined"
                    />
                  </FieldWrapper>
                </Grid>
                <Grid item xs={12}>
                  <FieldWrapper label="Password">
                    <Field
                      fullWidth
                      component={PasswordInputField}
                      name="password"
                      placeholder="Min. 8 characters"
                      variant="outlined"
                    />
                  </FieldWrapper>
                </Grid>
                <Grid item xs={12}>
                  <FieldWrapper label="Confirm password">
                    <Field
                      fullWidth
                      component={PasswordInputField}
                      name="confirmPassword"
                      placeholder="Confirm password"
                      variant="outlined"
                      inputProps={{
                        'data-testId': 'confirm-password-field',
                      }}
                    />
                  </FieldWrapper>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <AcceptTermsCheckbox handleOpenTerms={() => setTermsOpen(true)} />
            </Grid>
            <Grid item xs={12}>
              <ThemeButton
                loading={isSubmitting}
                size="large"
                color="PRIMARY"
                type="submit"
                className="button-wide"
              >
                Register
              </ThemeButton>
            </Grid>
            <Grid item xs={12}>
              <Link component={RouterLink} to="/login" color="secondary">
                Already registered? Login
              </Link>
            </Grid>
          </Grid>
          <TermsDialog open={termsOpen} onClose={() => setTermsOpen(false)} />
        </Form>
      )}
    </Formik>
  );
};

RegistrationInvitedForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
};

export default RegistrationInvitedForm;
